/* FOM · Command Center — "O que importa hoje".

   A porta de entrada por PRIORIDADE, ao lado da porta por estrutura (a cena
   organizacional continua sendo a entrada padrao do produto).

   Regra de verdade: cada fila e DERIVADA de dado que o mapa ja carregou ou
   de rotas que ja existem — nos (know, gaps, st, src, layer), investigacoes
   (PRIVATE), status do agente, publicacoes e trilha. Nada e inventado:
     · nenhum sinal comercial, lead, oportunidade, volume, tarifa ou score;
     · TO-BE nunca conta como realidade atual;
     · o que nao pode ser lido (camada PRIVATE para viewer) vira
       "nao disponivel" com o motivo, nunca zero.

   `derivarFilas` e PURA e testavel; `montarCommandCenter` so pinta. */
import type {
  FomNode, Relation, InvestigationResumo, BrainStatus, PublicationResumo,
  HistoryEntry, MemberRole, StatusClass,
} from '../core/types.ts';
import { COR_ST, ROTULO_ST } from '../core/types.ts';
import { ehFuturo } from '../core/lentes.ts';
import { guiaDe, idsComGuia } from '../dominio/guia-operacional.ts';
import { el, botao } from './dom.ts';

export type FilaItem = {
  id: string;
  rotulo: string;
  detalhe?: string;
  st?: StatusClass;
  acao: () => void;
};

export type Fila = {
  id: string;
  numero: string;
  unidade: string;
  titulo: string;
  porque: string;
  evidencia: string;
  estado: string;
  proximo: string;
  origem: 'base' | 'mapa' | 'privado' | 'na';
  itens: FilaItem[];
  /* quando a fila inteira nao pode ser lida ou nao existe */
  indisponivel?: string;
};

export type DadosCommandCenter = {
  nos: FomNode[];
  relacoes: Relation[];
  /* null = camada PRIVATE nao lida (viewer) ou falha de leitura */
  investigacoes: InvestigationResumo[] | null;
  brain: BrainStatus | null;
  publicacoes: PublicationResumo[] | null;
  historico: HistoryEntry[];
  papel: MemberRole;
};

export type AcoesCommandCenter = {
  aoIrParaNo: (externalId: string) => void;
  aoInvestigarLacuna: (no: FomNode, pergunta: string) => void;
  aoAbrirCamada: (aba: 'investigacao' | 'brain' | 'publicacao' | 'trilha', no: FomNode | null) => void;
  aoEntrarOrganizacao: () => void;
  aoEntrarProdutos: () => void;
  aoFechar: () => void;
};

const ST_ORDEM: StatusClass[] = ['E1', 'E2', 'E3', 'E4', 'ND', 'TOBE', 'MIX'];

/* ------------------------------------------------------------ derivacao -- */

function ativos(nos: FomNode[]): FomNode[] {
  return nos.filter((n) => !n.removed_at);
}

/* a "area" de um no: o ancestral de tipo unit/gest, ou o filho direto da
   raiz em cujo ramo o no vive. Derivacao por TIPO e por hierarquia — nunca
   por nome. */
export function areaDe(no: FomNode, porId: Map<string, FomNode>): FomNode | null {
  let atual: FomNode | undefined = no;
  let ultimoAbaixoDaRaiz: FomNode | null = null;
  let area: FomNode | null = null;
  let guarda = 0;
  while (atual && guarda++ < 40) {
    if (atual.node_type === 'unit' || atual.node_type === 'gest') area = atual;
    const pai: FomNode | undefined = atual.parent_external_id ? porId.get(atual.parent_external_id) : undefined;
    if (pai && !pai.parent_external_id) ultimoAbaixoDaRaiz = atual;
    atual = pai;
  }
  return area ?? ultimoAbaixoDaRaiz;
}

export function distribuicaoSt(nos: FomNode[]): Record<StatusClass, number> {
  const d = { E1: 0, E2: 0, E3: 0, E4: 0, ND: 0, TOBE: 0, MIX: 0 } as Record<StatusClass, number>;
  for (const n of nos) d[n.st] = (d[n.st] ?? 0) + 1;
  return d;
}

export function derivarFilas(dados: DadosCommandCenter, acoes: AcoesCommandCenter): Fila[] {
  const nos = ativos(dados.nos);
  const porId = new Map(nos.map((n) => [n.external_id, n]));
  const filas: Fila[] = [];

  /* 1 · lacunas por area */
  const porArea = new Map<string, { area: FomNode; lacunas: number; nos: number }>();
  let totalLacunas = 0;
  for (const n of nos) {
    if (!n.gaps.length) continue;
    totalLacunas += n.gaps.length;
    const a = areaDe(n, porId) ?? n;
    const r = porArea.get(a.external_id) ?? { area: a, lacunas: 0, nos: 0 };
    r.lacunas += n.gaps.length; r.nos += 1;
    porArea.set(a.external_id, r);
  }
  const areas = [...porArea.values()].sort((x, y) => y.lacunas - x.lacunas);
  filas.push({
    id: 'lacunas-por-area',
    numero: String(totalLacunas), unidade: 'lacunas abertas',
    titulo: 'Onde a empresa menos se conhece',
    porque: areas.slice(0, 3).map((a) => `${a.area.name} ${a.lacunas}`).join(' · ') || 'nenhuma lacuna declarada',
    evidencia: 'lacunas nao resolvidas, agrupadas pela area ancestral (tipo unidade/gestao)',
    estado: totalLacunas ? 'aberto' : 'nada a descobrir',
    proximo: 'investigar por area',
    origem: 'mapa',
    itens: areas.slice(0, 6).map((a) => ({
      id: a.area.external_id, rotulo: a.area.name, st: a.area.st,
      detalhe: `${a.lacunas} lacuna(s) em ${a.nos} no(s)`,
      acao: () => acoes.aoIrParaNo(a.area.external_id),
    })),
  });

  /* 2 · afirmacoes E1/E2 sem fonte declarada */
  const e12 = nos.filter((n) => n.st === 'E1' || n.st === 'E2');
  const semFonte = e12.filter((n) => !(n.src ?? '').trim());
  filas.push({
    id: 'sem-fonte',
    numero: String(semFonte.length), unidade: `de ${e12.length} em E1/E2`,
    titulo: 'Afirmacoes comprovadas sem fonte declarada',
    porque: semFonte.length
      ? 'classificacao E1/E2 com o campo de fonte vazio. Na BASE congelada isto e heranca do levantamento; para nos criados aqui o dominio exige referencia.'
      : 'toda afirmacao E1/E2 declara sua fonte.',
    evidencia: 'st ∈ {E1, E2} e src vazio — a mesma regra classificacao_sem_fonte do Operating Brain',
    estado: semFonte.length ? 'aberto' : 'ok',
    proximo: 'declarar a fonte (edicao humana; o agente nao afirma fonte)',
    origem: 'mapa',
    itens: semFonte.slice(0, 6).map((n) => ({
      id: n.external_id, rotulo: n.name, st: n.st, detalhe: n.origin === 'base' ? 'BASE congelada' : 'criado por voce',
      acao: () => acoes.aoIrParaNo(n.external_id),
    })),
  });

  /* 3 · investigacoes paradas (PRIVATE) */
  const invs = dados.investigacoes;
  const abertas = invs ? invs.filter((i) => i.status === 'aberta') : [];
  const respondidas = invs ? invs.filter((i) => i.status === 'respondida') : [];
  const maisAntiga = abertas.slice().sort((a, b) => a.created_at.localeCompare(b.created_at))[0];
  const dias = maisAntiga ? Math.max(0, Math.floor((Date.now() - Date.parse(maisAntiga.created_at)) / 86400000)) : 0;
  filas.push({
    id: 'investigacoes-paradas',
    numero: invs ? String(abertas.length) : '—', unidade: 'abertas sem resposta',
    titulo: 'Investigacoes paradas',
    porque: !invs
      ? 'a camada privada nao foi lida.'
      : abertas.length
        ? `${abertas.length} pergunta(s) sem resposta; a mais antiga ha ${dias} dia(s).${respondidas.length ? ` ${respondidas.length} respondida(s) aguardam encerramento.` : ''}`
        : `nenhuma pergunta aberta.${respondidas.length ? ` ${respondidas.length} respondida(s) aguardam encerramento.` : ''}`,
    evidencia: 'status da investigacao (aberta = sem resposta); camada PRIVATE, disponivel para Editor ou Owner',
    estado: !invs ? 'nao disponivel' : abertas.length ? 'parado' : 'nada parado',
    proximo: 'registrar evidencia e responder',
    origem: 'privado',
    ...(invs ? {} : { indisponivel: dados.papel === 'viewer'
      ? 'Investigacoes sao camada privada — disponiveis para Editor ou Owner.'
      : 'Nao foi possivel ler as investigacoes agora.' }),
    itens: [...abertas, ...respondidas].slice(0, 6).map((i) => ({
      id: i.code, rotulo: `${i.code} · ${i.question}`, detalhe: i.status === 'aberta' ? 'sem resposta' : 'respondida · nao encerrada',
      acao: () => acoes.aoAbrirCamada('investigacao', porId.get(i.node_external_id) ?? null),
    })),
  });

  /* 4 · divergencia documentado x observado (guia com fonte por item) */
  const divergencias: { no: FomNode; pergunta: string; doc: string; obs: string }[] = [];
  for (const id of idsComGuia()) {
    const no = porId.get(id);
    const g = guiaDe(id);
    if (!no || !g?.divergencias?.length) continue;
    for (const d of g.divergencias) divergencias.push({ no, pergunta: d.pergunta, doc: d.documentado.t, obs: d.observado.t });
  }
  filas.push({
    id: 'divergencias',
    numero: String(divergencias.length), unidade: 'divergencia(s)',
    titulo: 'Regra documentada × pratica observada',
    porque: divergencias.length
      ? divergencias.map((d) => `${d.no.name}: ${d.pergunta}`).join(' · ')
      : 'nenhuma divergencia declarada entre documentado (E2) e observado (E1).',
    evidencia: 'guia operacional: lado documentado (E2, com fonte) × lado observado (E1, com fonte). Merece investigacao, nao conclusao',
    estado: divergencias.length ? 'sem pergunta aberta' : 'ok',
    proximo: 'investigar com a pergunta pre-preenchida',
    origem: 'base',
    itens: divergencias.slice(0, 6).map((d) => ({
      id: d.no.external_id, rotulo: d.no.name, st: d.no.st, detalhe: d.pergunta,
      acao: () => acoes.aoInvestigarLacuna(d.no, d.pergunta),
    })),
  });

  /* 5 · futuro sem lastro: TO-BE sem investigacao aberta */
  const futuros = nos.filter((n) => ehFuturo(n));
  const comInv = new Set((invs ?? []).filter((i) => i.status !== 'encerrada').map((i) => i.node_external_id));
  const semLastro = futuros.filter((n) => !comInv.has(n.external_id));
  filas.push({
    id: 'futuro-sem-lastro',
    numero: String(invs ? semLastro.length : futuros.length), unidade: invs ? 'TO-BE sem investigacao' : 'TO-BE declarados',
    titulo: 'Futuro sem lastro',
    porque: futuros.length
      ? `${futuros.length} intencao(oes) declarada(s) como TO-BE${invs ? `; ${semLastro.length} sem nenhuma investigacao aberta` : ' (investigacoes nao lidas)'}. Intencao nao e operacao implantada.`
      : 'nenhuma intencao futura declarada.',
    evidencia: 'layer = tobe ou st = TOBE; investigacao aberta por no (camada PRIVATE)',
    estado: futuros.length ? 'aberto' : 'ok',
    proximo: 'decidir o que provar primeiro',
    origem: 'mapa',
    itens: (invs ? semLastro : futuros).slice(0, 6).map((n) => ({
      id: n.external_id, rotulo: n.name, st: 'TOBE' as StatusClass, detalhe: n.origin === 'base' ? 'BASE congelada' : 'criado por voce',
      acao: () => acoes.aoIrParaNo(n.external_id),
    })),
  });

  /* 6 · propostas do agente */
  const b = dados.brain;
  filas.push({
    id: 'propostas-do-agente',
    numero: b?.granted ? String(b.pending) : '—', unidade: 'propostas aguardando decisao',
    titulo: 'O que o agente propoe',
    porque: b?.granted
      ? (b.pending ? `${b.pending} proposta(s) aguardam uma pessoa decidir. Autoridade efetiva: ${b.effective_role ?? '—'}.` : 'nenhuma proposta pendente.')
      : 'o agente nao tem concessao nesta organizacao. Nada foi analisado; nada e proposto.',
    evidencia: 'status do Operating Brain (concessao, teto, autonomia, pendentes). Proposta nunca e fato',
    estado: b?.granted ? (b.pending ? 'aguarda voce' : 'sem pendencia') : 'inativo',
    proximo: b?.granted ? 'aprovar ou recusar, uma a uma' : 'conceder e ato do Owner (decisao separada)',
    origem: 'privado',
    ...(b?.granted ? {} : { indisponivel: 'Agente inativo: sem concessao nesta organizacao. O que aparecer aqui sera ANALISE, nunca dado do mapa.' }),
    itens: [],
  });

  /* 7 · publicacao desatualizada */
  const pubs = dados.publicacoes;
  const ultima = pubs && pubs.length ? pubs[0]! : null;
  const ultimoEvento = dados.historico[0]?.at ?? null;
  const desatualizada = !!(ultima && ultimoEvento && ultimoEvento > ultima.published_at);
  filas.push({
    id: 'publicacao',
    numero: ultima ? 'v' + ultima.version : '—', unidade: ultima ? ultima.published_at.slice(0, 10) : 'nenhuma publicacao',
    titulo: desatualizada ? 'Publicacao desatualizada' : ultima ? 'Publicacao em dia' : 'Mapa nunca publicado',
    porque: !pubs
      ? 'nao foi possivel ler as publicacoes.'
      : !ultima
        ? 'o que o mundo pode ver ainda nao existe: nenhuma versao foi publicada.'
        : desatualizada
          ? `o mapa mudou depois da v${ultima.version} (ultima alteracao ${ultimoEvento!.slice(0, 16).replace('T', ' ')}).`
          : `nenhuma alteracao registrada depois da v${ultima.version}.`,
    evidencia: 'data da ultima publicacao × ultima entrada da trilha (append-only)',
    estado: !ultima ? 'nunca publicado' : desatualizada ? 'desatualizada' : 'em dia',
    proximo: dados.papel === 'owner' ? 'publicar nova versao' : 'publicar exige Owner',
    origem: 'mapa',
    itens: [{
      id: 'publicacao', rotulo: ultima ? `Ver publicacoes (ate v${ultima.version})` : 'Abrir publicacoes',
      acao: () => acoes.aoAbrirCamada('publicacao', null),
    }],
  });

  return filas;
}

/* -------------------------------------------------------------- pintura -- */

function chipSt(st: StatusClass): HTMLElement {
  const c = el('span', 'ev-chip ev-' + st, ROTULO_ST[st]);
  c.style.setProperty('--cor', COR_ST[st]);
  return c;
}

function barraEpistemica(d: Record<StatusClass, number>): HTMLElement {
  const total = ST_ORDEM.reduce((a, s) => a + (d[s] ?? 0), 0) || 1;
  const bar = el('div', 'ev-barra');
  for (const s of ST_ORDEM) {
    if (!d[s]) continue;
    const i = el('i');
    i.style.width = ((d[s] / total) * 100).toFixed(1) + '%';
    i.style.background = COR_ST[s];
    i.title = `${ROTULO_ST[s]} · ${d[s]}`;
    bar.appendChild(i);
  }
  return bar;
}

function legendaEpistemica(d: Record<StatusClass, number>): HTMLElement {
  const lg = el('div', 'ev-legenda');
  for (const s of ST_ORDEM) {
    if (!d[s]) continue;
    const item = el('span');
    const ponto = el('i');
    ponto.style.background = COR_ST[s];
    item.appendChild(ponto);
    item.appendChild(document.createTextNode(`${s} ${d[s]}`));
    lg.appendChild(item);
  }
  return lg;
}

export function montarCommandCenter(host: HTMLElement, acoes: AcoesCommandCenter) {
  host.classList.add('command-center');
  host.hidden = true;
  host.setAttribute('role', 'region');
  host.setAttribute('aria-label', 'Command Center — o que importa hoje');

  function render(dados: DadosCommandCenter): void {
    host.innerHTML = '';
    const nos = ativos(dados.nos);
    const filas = derivarFilas(dados, acoes);
    const hoje = new Date();
    const data = hoje.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });

    const cabecalho = el('header', 'cc-cabecalho');
    const titulos = el('div');
    titulos.appendChild(el('p', 'cc-eyebrow', `Command Center · ${data}`));
    titulos.appendChild(el('h1', 'cc-titulo', 'O que importa hoje'));
    titulos.appendChild(el('p', 'cc-sub',
      'Filas derivadas do que o mapa ja sabe: nos, conhecimento, lacunas, classificacao, camada, investigacoes, publicacao e agente. Cada item diz o que, por que e leva ao objeto. Nenhum numero sem origem.'));
    cabecalho.appendChild(titulos);
    const acoesTopo = el('div', 'cc-acoes');
    acoesTopo.appendChild(botao('Abrir a organizacao', acoes.aoEntrarOrganizacao, 'botao'));
    acoesTopo.appendChild(botao('Produtos', acoes.aoEntrarProdutos, 'botao'));
    const fecharBtn = botao('Voltar ao mapa · Esc', acoes.aoFechar, 'botao botao-principal');
    acoesTopo.appendChild(fecharBtn);
    cabecalho.appendChild(acoesTopo);
    host.appendChild(cabecalho);

    const corpo = el('div', 'cc-corpo');
    const lista = el('div', 'cc-filas');
    const atencao = filas.filter((f) => !f.indisponivel && f.numero !== '0' && f.estado !== 'ok' && f.estado !== 'em dia');
    host.dataset.atencao = String(atencao.length);

    for (const f of filas) {
      const item = el('article', 'fila' + (f.indisponivel ? ' fila-indisponivel' : ''));
      item.dataset.fila = f.id;
      const num = el('div', 'fila-numero');
      num.appendChild(el('b', undefined, f.numero));
      num.appendChild(el('small', undefined, f.unidade));
      item.appendChild(num);

      const meio = el('div', 'fila-meio');
      meio.appendChild(el('h2', 'fila-titulo', f.titulo));
      meio.appendChild(el('p', 'fila-porque', f.porque));
      const meta = el('div', 'fila-meta');
      meta.appendChild(el('span', 'fila-evidencia', 'evidencia: ' + f.evidencia));
      const origem = el('span', 'cc-tag cc-tag-' + f.origem,
        f.origem === 'base' ? 'BASE + guia' : f.origem === 'mapa' ? 'dado do mapa' : f.origem === 'privado' ? 'camada privada' : 'nao disponivel');
      meta.appendChild(origem);
      meta.appendChild(el('span', 'fila-estado', 'estado: ' + f.estado));
      meta.appendChild(el('span', 'fila-proximo', 'proximo: ' + f.proximo));
      meio.appendChild(meta);
      if (f.indisponivel) {
        meio.appendChild(el('p', 'fila-aviso', f.indisponivel));
      } else if (f.itens.length) {
        const ul = el('ul', 'fila-itens');
        for (const it of f.itens) {
          const li = el('li');
          const b = el('button', 'fila-item');
          b.type = 'button';
          if (it.st) b.appendChild(chipSt(it.st));
          b.appendChild(el('span', 'fila-item-rotulo', it.rotulo));
          if (it.detalhe) b.appendChild(el('span', 'fila-item-detalhe', it.detalhe));
          b.addEventListener('click', it.acao);
          li.appendChild(b);
          ul.appendChild(li);
        }
        meio.appendChild(ul);
      }
      item.appendChild(meio);
      lista.appendChild(item);
    }
    corpo.appendChild(lista);

    /* lateral: estado do conhecimento, agente, fronteira FOM/FOE */
    const lado = el('aside', 'cc-lado');
    const painelEstado = el('section', 'cc-painel');
    painelEstado.appendChild(el('p', 'cc-eyebrow', 'Estado do conhecimento · mapa'));
    const dist = distribuicaoSt(nos);
    painelEstado.appendChild(barraEpistemica(dist));
    painelEstado.appendChild(legendaEpistemica(dist));
    const mini = el('div', 'cc-mini');
    const know = nos.reduce((a, n) => a + n.know.length, 0);
    const gaps = nos.reduce((a, n) => a + n.gaps.length, 0);
    const base = nos.filter((n) => n.origin === 'base').length;
    const user = nos.length - base;
    for (const [v, r] of [[String(nos.length), `elementos · ${base} BASE · ${user} USER`], [String(dados.relacoes.filter((r) => nos.some((n) => n.external_id === r.from) && nos.some((n) => n.external_id === r.to)).length), 'relacoes'], [String(know), 'conhecimentos'], [String(gaps), 'lacunas']]) {
      const d = el('div');
      d.appendChild(el('b', undefined, v));
      d.appendChild(el('span', undefined, r));
      mini.appendChild(d);
    }
    painelEstado.appendChild(mini);
    painelEstado.appendChild(el('p', 'cc-nota', 'TO-BE e intencao declarada e nunca conta como atual. E3/E4 sao relato e hipotese, nao fato.'));
    lado.appendChild(painelEstado);

    const painelAgente = el('section', 'cc-painel cc-painel-agente');
    const cab = el('div', 'cc-painel-cab');
    cab.appendChild(el('p', 'cc-eyebrow', 'Agente · Operating Brain'));
    cab.appendChild(el('span', 'cc-tag ' + (dados.brain?.granted ? 'cc-tag-mapa' : 'cc-tag-na'), dados.brain?.granted ? 'ativo' : 'inativo'));
    painelAgente.appendChild(cab);
    painelAgente.appendChild(el('p', 'cc-moldura-agente',
      'O que aparece aqui e ANALISE, nao dado do mapa. O agente observa e propoe; nada vira conhecimento, classificacao ou publicacao sem uma pessoa decidir.'));
    painelAgente.appendChild(botao('Ver o Operating Brain', () => acoes.aoAbrirCamada('brain', null), 'botao'));
    lado.appendChild(painelAgente);

    const painelFoe = el('section', 'cc-painel');
    const cabFoe = el('div', 'cc-painel-cab');
    cabFoe.appendChild(el('p', 'cc-eyebrow', 'Fronteira FOM ↔ FOE'));
    cabFoe.appendChild(el('span', 'cc-tag cc-tag-conceitual', 'conceitual'));
    painelFoe.appendChild(cabFoe);
    const fr = el('dl', 'cc-fronteira');
    for (const [k, v] of [['FOM', 'conhece · contextualiza · governa · explica · recomenda'], ['FOE', 'executa · prospecta · atua no mercado']]) {
      fr.appendChild(el('dt', undefined, k));
      fr.appendChild(el('dd', undefined, v));
    }
    painelFoe.appendChild(fr);
    painelFoe.appendChild(el('p', 'cc-nota',
      'Sinais comerciais, oportunidades, tarifas e volumes nao existem no FOM. Chegariam por interface governada, com classe epistemica e autoridade declaradas. Nada disso esta implementado.'));
    lado.appendChild(painelFoe);

    corpo.appendChild(lado);
    host.appendChild(corpo);
  }

  return {
    render,
    mostrar(): void { host.hidden = false; },
    esconder(): void { host.hidden = true; },
    get aberta(): boolean { return !host.hidden; },
  };
}
